import { API, Hub } from "aws-amplify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GetPodSession,
  PodSessionsByOrganizationId,
} from "../../graphql/queries";
import {
  onCreatePodSession,
  onLitePodSessionCreate,
  onPodSessionClaim,
  onUpdatePodSession,
} from "../../graphql/subscriptions";
import {
  claimPodSessionAndNotify,
  fetchOrCreateUser,
  fetchPodSessionClaimState,
  PodSessionAlreadyClaimedError,
} from "../PodV2/api";
import { SavedToast, useSavedToast } from "../PodV2/SavedToast";
import {
  applyQueueEvent,
  bindGraphqlSubscription,
  buildInitialQueue,
  isQueueRowClaimed,
  readMissingQueueSessions,
  reconcileExistingQueue,
} from "../PodV2/podQueueList";
import {
  QueueClaimButton,
  QueueDataTable,
  waitingColumn,
} from "./QueueSessionList";
import {
  commitQueueState,
  useClaimConfirmations,
} from "./useClaimConfirmations";
import {
  getEmailUsername,
  getProctorUsername,
  isUnclaimedProctor,
} from "../PodV2/sessionUtils";
import {
  Box,
  Button,
  Data,
  DataSearch,
  DataSummary,
  Text,
  Toolbar,
} from "grommet";

const ORGANIZATION_ID = "college-board";
const PAGE_LIMIT = 300;
const MAX_PAGES = 10;
const REFETCH_DEBOUNCE_MS = 1000;

function getUpdatedAtMs(session) {
  const updatedAtMs = new Date(session?.updatedAt ?? 0).getTime();
  return Number.isNaN(updatedAtMs) ? 0 : updatedAtMs;
}

export function mergeSessions(current, incoming) {
  const byId = new Map(current.map((session) => [session.id, session]));

  for (const session of incoming) {
    if (!session?.id) {
      continue;
    }

    const existing = byId.get(session.id);
    if (!existing || getUpdatedAtMs(session) >= getUpdatedAtMs(existing)) {
      byId.set(session.id, session);
    }
  }

  return Array.from(byId.values());
}

function normalizeLiteSession(session) {
  return {
    ...session,
    proctor: session.proctor ?? null,
  };
}

function normalizeProctorName(proctor) {
  return String(proctor ?? "").trim().toLowerCase();
}

function isClaimedByMe(proctor, myProctorUsernames) {
  return myProctorUsernames.has(normalizeProctorName(proctor));
}

const dataProperties = {
  id: {
    label: "ID",
    property: "id",
    filter: false,
    search: false,
  },
  testerName: {
    label: "Tester Name",
    property: "testerName",
    filter: false,
  },
  testerEmail: {
    label: "Tester Email",
    property: "testerEmail",
    filter: false,
  },
  organizationName: {
    label: "Organization Name",
    property: "organizationName",
    filter: true,
  },
  proctor: {
    label: "Proctor",
    property: "proctor",
    filter: false,
  },
};

export default function PodQueueCollegeBoard({
  visible = true,
  notTawkOrganizationIds,
}) {
  const [queueState, setQueueState] = useState({
    sessions: [],
    claimedIds: new Set(),
    pendingClaims: new Map(),
    claimVersions: new Map(),
    readRequests: [],
    loaded: false,
  });
  const sessions = queueState.sessions;
  const claimedIds = queueState.claimedIds;
  const queueStateRef = useRef(queueState);
  queueStateRef.current = queueState;
  const rememberQueueEvent = useCallback((event) => {
    if (event?.claimGeneration != null) {
      queueEventsRef.current.push(event);
    }
    setQueueState((current) =>
      commitQueueState(
        current,
        applyQueueEvent(
          current.sessions,
          current.claimedIds,
          event,
          current.pendingClaims,
          current.claimVersions,
        ),
      ),
    );
  }, []);

  const consumeReadRequests = useCallback((done) => {
    setQueueState((current) => ({
      ...current,
      readRequests: (current.readRequests ?? []).filter(
        (item) =>
          !done.some(
            (finished) =>
              finished.id === item.id && finished.generation === item.generation,
          ),
      ),
    }));
  }, []);

  useClaimConfirmations({
    readRequests: queueState.readRequests,
    onSession: rememberQueueEvent,
    onConsumed: consumeReadRequests,
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const [proctorUsername, setProctorUsername] = useState(null);
  const [myProctorUsernames, setMyProctorUsernames] = useState(
    () => new Set(),
  );
  const refetchTimeoutRef = useRef(null);
  const backgroundFetchRef = useRef(false);
  const queueEventsRef = useRef([]);
  const queueFetchRef = useRef(null);
  const queueReconcileAgainRef = useRef(false);
  const { showSaved, savedToastProps } = useSavedToast();

  const mergeIncomingSession = useCallback((session) => {
    if (!session?.id) return;
    const event = { type: "update", session };
    queueEventsRef.current.push(event);
    rememberQueueEvent(event);
  }, [rememberQueueEvent]);

  const fetchCollegeBoardSessions = useCallback(async () => {
    if (queueFetchRef.current) {
      queueReconcileAgainRef.current = true;
      return queueFetchRef.current;
    }

    const run = async () => {
      queueEventsRef.current = [];
      const pages = [];
      let nextToken = null;

      for (let page = 0; page < MAX_PAGES; page += 1) {
        const { data } = await API.graphql({
          query: PodSessionsByOrganizationId,
          variables: {
            organizationId: ORGANIZATION_ID,
            sortDirection: "DESC",
            limit: PAGE_LIMIT,
            nextToken: nextToken || undefined,
          },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });

        pages.push(data.podSessionByOrganizationId?.items ?? []);
        nextToken = data.podSessionByOrganizationId?.nextToken ?? null;
        if (!nextToken) break;
      }

      const snapshot = queueStateRef.current;
      if (snapshot.loaded) {
        const confirmed = await readMissingQueueSessions(
          snapshot.sessions,
          pages,
          async (id) => {
            const { data } = await API.graphql({
              query: GetPodSession,
              variables: { id },
              authMode: "AMAZON_COGNITO_USER_POOLS",
            });
            return data?.getPodSession ?? null;
          },
        );
        if (confirmed.length) pages.push(confirmed);
      }

      const duringFetch = queueEventsRef.current.slice();
      queueEventsRef.current = queueEventsRef.current.slice(duringFetch.length);
      const trailing = queueEventsRef.current.splice(0);
      const events = duringFetch.concat(trailing);
      setQueueState((current) => {
        const next = current.loaded
          ? reconcileExistingQueue(
              current.sessions,
              current.claimedIds,
              pages,
              events,
              current.pendingClaims,
              current.claimVersions,
            )
          : buildInitialQueue(pages, events, { includeClaimed: true });
        return commitQueueState(current, { ...next, loaded: true });
      });
    };

    const promise = run().finally(() => {
      queueFetchRef.current = null;
      if (queueReconcileAgainRef.current) {
        queueReconcileAgainRef.current = false;
        fetchCollegeBoardSessions();
      }
    });
    queueFetchRef.current = promise;
    return promise;
  }, [rememberQueueEvent]);

  const scheduleRefetch = useCallback(() => {
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
    }

    refetchTimeoutRef.current = setTimeout(() => {
      refetchTimeoutRef.current = null;

      if (backgroundFetchRef.current) {
        return;
      }

      backgroundFetchRef.current = true;

      fetchCollegeBoardSessions()
        .catch((error) => {
          console.error("Error refetching College Board sessions:", error);
        })
        .finally(() => {
          backgroundFetchRef.current = false;
        });
    }, REFETCH_DEBOUNCE_MS);
  }, [fetchCollegeBoardSessions]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);

    try {
      await fetchCollegeBoardSessions();
    } catch (error) {
      console.error("Error refreshing College Board sessions:", error);
    } finally {
      setRefreshing(false);
    }
  }, [fetchCollegeBoardSessions]);

  useEffect(() => {
    let cancelled = false;

    const loadProctorIdentity = async () => {
      try {
        const { user, email } = await fetchOrCreateUser();
        if (cancelled) {
          return;
        }

        const username = getProctorUsername(user, email);
        const usernames = new Set(
          [username, getEmailUsername(email)]
            .filter(Boolean)
            .map(normalizeProctorName),
        );

        setProctorUsername(username);
        setMyProctorUsernames(usernames);
      } catch (error) {
        console.error("Error loading proctor identity:", error);
      }
    };

    loadProctorIdentity();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleViewSession = useCallback(
    async (session) => {
      const sessionId = session?.id;
      if (!sessionId) {
        return;
      }

      setClaimingId(sessionId);

      const placeholderTab = window.open("", "_blank");
      if (!placeholderTab) {
        console.warn(
          "[PodQueueCollegeBoard] Popup blocked while opening session",
          sessionId,
        );
      }

      const navigateToSession = () => {
        const url = `/pod-session-v2?id=${sessionId}`;
        if (placeholderTab) {
          placeholderTab.location.href = url;
          return;
        }

        window.open(url, "_blank");
      };

      const handleAlreadyClaimed = (claimedSession) => {
        if (claimedSession?.id) {
          mergeIncomingSession(claimedSession);
        }

        const claimEvent = { type: "claim", id: sessionId };
        queueEventsRef.current.push(claimEvent);
        rememberQueueEvent(claimEvent);

        const proctorName =
          claimedSession?.proctor || session.proctor || "another proctor";
        showSaved(`Claimed by ${proctorName}`);
        navigateToSession();
      };

      try {
        if (!isUnclaimedProctor(session.proctor)) {
          if (isClaimedByMe(session.proctor, myProctorUsernames)) {
            navigateToSession();
            return;
          }

          handleAlreadyClaimed(session);
          return;
        }

        const claimState = await fetchPodSessionClaimState(sessionId);

        if (!claimState) {
          navigateToSession();
          return;
        }

        if (!isUnclaimedProctor(claimState.proctor)) {
          if (isClaimedByMe(claimState.proctor, myProctorUsernames)) {
            mergeIncomingSession(claimState);
            navigateToSession();
            return;
          }

          handleAlreadyClaimed(claimState);
          return;
        }

        if (!proctorUsername) {
          navigateToSession();
          return;
        }

        try {
          const { notificationError } = await claimPodSessionAndNotify(
            sessionId,
            proctorUsername,
            { expectedProctor: claimState.proctor },
          );
          const claimEvent = { type: "claim", id: sessionId };
          queueEventsRef.current.push(claimEvent);
          rememberQueueEvent(claimEvent);
          if (notificationError) {
            showSaved(
              "Session claimed, but other proctors were not notified.",
            );
          }
          navigateToSession();
        } catch (error) {
          if (error instanceof PodSessionAlreadyClaimedError) {
            handleAlreadyClaimed(error.session);
            return;
          }

          throw error;
        }
      } catch (error) {
        console.error("Error opening College Board session:", error);
        navigateToSession();
      } finally {
        setClaimingId(null);
      }
    },
    [
      mergeIncomingSession,
      myProctorUsernames,
      proctorUsername,
      rememberQueueEvent,
      showSaved,
    ],
  );

  useEffect(() => {
    let cancelled = false;

    const loadSessions = async () => {
      setLoading(true);

      try {
        await fetchCollegeBoardSessions();
      } catch (error) {
        console.error("Error fetching College Board sessions:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSessions();

    return () => {
      cancelled = true;
    };
  }, [fetchCollegeBoardSessions]);

  useEffect(() => {
    let connectionDisrupted = false;
    const organizationFilter = {
      organizationId: { eq: ORGANIZATION_ID },
    };

    const record = (event) => {
      queueEventsRef.current.push(event);
      rememberQueueEvent(event);
    };

    const belongsToCollegeBoard = (session) =>
      session?.organizationId === ORGANIZATION_ID;

    const createSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: {
        query: onCreatePodSession,
        variables: { filter: organizationFilter },
      },
      onNext: ({ value }) => {
        const session = value?.data?.onCreatePodSession;
        if (!belongsToCollegeBoard(session)) return;
        record({ type: "create", session });
      },
      onRestart: () => fetchCollegeBoardSessions(),
    });

    const liteCreateSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: {
        query: onLitePodSessionCreate,
        variables: { type: "PodSession" },
      },
      onNext: ({ value }) => {
        const session = normalizeLiteSession(value?.data?.onLitePodSessionCreate);
        if (!belongsToCollegeBoard(session)) return;
        record({ type: "create", session });
      },
      onRestart: () => fetchCollegeBoardSessions(),
    });

    const claimSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: { query: onPodSessionClaim },
      onNext: ({ value }) => {
        const sessionId = value?.data?.onClaim?.id;
        if (!sessionId) return;
        record({ type: "claim", id: sessionId });
      },
      onRestart: () => fetchCollegeBoardSessions(),
    });

    const updateSubscription = bindGraphqlSubscription({
      graphql: API.graphql.bind(API),
      operation: {
        query: onUpdatePodSession,
        variables: { filter: organizationFilter },
      },
      onNext: ({ value }) => {
        const session = value?.data?.onUpdatePodSession;
        if (!session?.id || !belongsToCollegeBoard(session)) return;
        record({ type: "update", session });
      },
      onRestart: () => fetchCollegeBoardSessions(),
    });

    const removeHubListener = Hub.listen("api", ({ payload }) => {
      if (payload.event !== "ConnectionStateChange") return;
      const state = payload.data?.connectionState;
      if (
        state === "ConnectionDisrupted" ||
        state === "ConnectionDisruptedPendingNetwork"
      ) {
        connectionDisrupted = true;
        return;
      }
      if (state === "Connected" && connectionDisrupted) {
        connectionDisrupted = false;
        fetchCollegeBoardSessions();
      }
    });

    return () => {
      createSubscription.unsubscribe();
      liteCreateSubscription.unsubscribe();
      claimSubscription.unsubscribe();
      updateSubscription.unsubscribe();
      removeHubListener();
    };
  }, [fetchCollegeBoardSessions, rememberQueueEvent]);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        scheduleRefetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);

      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current);
        refetchTimeoutRef.current = null;
      }
    };
  }, [scheduleRefetch]);

  const displayedSessions = sessions;

  const columns = useMemo(
    () => [
      {
        property: "actions",
        header: <Text>Actions</Text>,
        render: (datum) => (
          <QueueClaimButton
            session={datum}
            claimed={isQueueRowClaimed(claimedIds, datum.id)}
            claiming={claimingId === datum.id}
            onClaim={handleViewSession}
          />
        ),
        align: "start",
        alignVertical: "middle",
        size: "small",
      },
      waitingColumn({ claimedIds, viewVisible: visible }),
      {
        property: "testerName",
        header: <Text>Tester Name</Text>,
        render: (datum) => (
          <Text size="small" truncate>
            {datum.testerName}
          </Text>
        ),
        align: "start",
        alignVertical: "middle",
        size: "medium",
      },
      {
        property: "testerEmail",
        header: <Text>Tester Email</Text>,
        render: (datum) => (
          <Text size="small" truncate>
            {datum.testerEmail}
          </Text>
        ),
        align: "start",
        alignVertical: "middle",
        size: "medium",
      },
      {
        property: "organizationName",
        header: <Text>Organization Name</Text>,
        render: (datum) => (
          <Text size="small" truncate>
            {datum.organizationName}
          </Text>
        ),
        align: "start",
        alignVertical: "middle",
        size: "medium",
      },
      {
        property: "proctor",
        header: <Text>Proctor</Text>,
        render: (datum) => (
          <Text size="small" truncate>
            {isUnclaimedProctor(datum.proctor) ? "—" : datum.proctor}
          </Text>
        ),
        align: "start",
        alignVertical: "middle",
        size: "medium",
      },
    ],
    [claimingId, claimedIds, handleViewSession, visible],
  );

  if (loading && sessions.length === 0) {
    return (
      <Box pad="medium">
        <Text>Loading College Board sessions...</Text>
      </Box>
    );
  }

  return (
    <>
      <Data data={displayedSessions} properties={dataProperties}>
        <Toolbar>
          <DataSearch placeholder="Search" />
          <DataSummary />
          <Button
            label="Refresh"
            onClick={handleRefresh}
            disabled={refreshing}
          />
        </Toolbar>
        <QueueDataTable
          sessions={displayedSessions}
          claimedIds={claimedIds}
          columns={columns}
          notTawkOrganizationIds={notTawkOrganizationIds}
          useFilteredData
        />
      </Data>
      <SavedToast {...savedToastProps} />
    </>
  );
}
